import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    AlertType,
    IAlarmDefinitionChild,
    IBandDefinitionChild,
    IIndicatorResponse,
    IUnitResponse,
    MetricClassificationSourceEnum,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { GenericHierarchyChild } from 'utils/group_hierarchy';

export type HierarchyInfo =
    | {
          type: 'prometheus';
          hierarchy: GenericHierarchyChild[];
      }
    | {
          type: 'data-platform';
          hierarchy: Omit<GenericHierarchyChild, 'HierarchyType'>[];
      };

export type GeneralIndicatorInfo = Readonly<{
    Id: string;
    Name: string;
    IndicatorType?: MetricTypeCodes;
    DisplayUnits?: string;
    Statistic?: DataPlatformApi.Statistic;
    PhysicalQuantity?: string;
    PhysicalQuantityType?: string;
    HierarchyInfo: HierarchyInfo;
    AlarmDefinition?: IAlarmDefinitionChild;
    HasSpectrum: boolean;
    HasBaseline?: boolean;
    IsVibration: boolean;
    IsHardwareFiltered?: boolean;
    BandDefinition?: IBandDefinitionChild;
    AlertStatus?: AlertType;
}>;

export const mapPromIndicatorToGeneralIndicatorInfo = (
    indicator: IIndicatorResponse,
): GeneralIndicatorInfo => {
    return {
        ...indicator,
        HierarchyInfo: {
            type: 'prometheus',
            hierarchy: indicator.Hierarchy,
        },
    };
};

export const mapDataPlatformAlertConfigToAlarmDefinition = (
    alertConfig: DataPlatformApi.AlertConfig,
): IAlarmDefinitionChild => {
    return {
        CriticalLowAlarmValue: alertConfig.lowCriticalAlarm ?? undefined,
        LowAlarmValue: alertConfig.lowAlarm ?? undefined,
        LowWarningValue: alertConfig.lowWarning ?? undefined,
        HighWarningValue: alertConfig.highWarning ?? undefined,
        HighAlarmValue: alertConfig.highAlarm ?? undefined,
        CriticalHighAlarmValue: alertConfig.highCriticalAlarm ?? undefined,
        Classification: alertConfig.off
            ? MetricClassificationSourceEnum.Self
            : MetricClassificationSourceEnum.None,
        OffThresholdValue: alertConfig.off ?? undefined,
        NotificationCriteriaPoints: alertConfig.minPoints ?? undefined,
        NotificationCriteriaPercentage: alertConfig.minPointRatio
            ? alertConfig.minPointRatio * 100
            : undefined,
    };
};

export const mapDataPlatformHierarchyToHierarchyChild = (
    items: DataPlatformApi.Group[],
): Omit<GenericHierarchyChild, 'HierarchyType'>[] =>
    items.map((item) => ({
        HierarchyId: item.id,
        HierarchyName: item.name,
        HierarchyAbbreviation: item.abbreviation,
    }));

export const mapDataPlatformIndicatorToGeneralIndicatorInfo = (
    indicator: DataPlatformApi.IndicatorResponse,
    unitsRequest?: Record<string, string>,
    units?: Map<string, IUnitResponse>,
): GeneralIndicatorInfo => {
    // The data platform always returns indicator units in their base units,
    // so we need to map the base unit to the user's preferred unit for display purposes.
    const unitCode = unitsRequest?.[indicator.physicalQuantity] || indicator.unit.code;
    const unit = units?.get(unitCode);

    return {
        Id: indicator.id,
        Name: indicator.name,
        HierarchyInfo: {
            type: 'data-platform',
            hierarchy: mapDataPlatformHierarchyToHierarchyChild(indicator.groupHierarchy),
        },
        AlarmDefinition: mapDataPlatformAlertConfigToAlarmDefinition(
            indicator.alertConfig,
        ),
        HasSpectrum: indicator.statistic !== 'value',
        IsVibration:
            indicator.physicalQuantity === 'acceleration' ||
            indicator.physicalQuantity === 'velocity',
        Statistic: indicator.statistic,
        PhysicalQuantity: indicator.physicalQuantity,
        PhysicalQuantityType: indicator.physicalQuantityType ?? undefined,
        HasBaseline: indicator.hasBaseline,
        DisplayUnits: unit?.Abbreviation ?? indicator.unit.abbreviation,
        BandDefinition: indicator.band
            ? {
                  HighBandFrequency: indicator.band.max,
                  LowBandFrequency: indicator.band.min,
              }
            : undefined,
        IsHardwareFiltered: indicator.filtered,
    };
};
